/**
 * Iraksak düşünme: alternatif kullanım ve yarım çizgiyi tamamlama.
 *
 * İKİSİ DE YALNIZCA KÂĞIT VE ELLE PUANLANIYOR, ve bu bir eksiklik
 * değil bir karar. İkisinin de cevabı açık uçlu: birinde "tuğlayla ne
 * yapılabilir", ötekinde bir resim. Otomatik puanlamanın yapabileceği
 * tek şey cevabın BİÇİMİNE bakmak olurdu — kaç satır doldu, kaç harf
 * yazıldı — ve o sayı yaratıcılıkla ilgili hiçbir şey söylemiyor.
 *
 * İstek listesi burada bir dil modeli öneriyordu: "hastanın yazdığı
 * cevapların ne kadar orijinal olduğunu anlamsal olarak analiz edip
 * otomatik yaratıcılık puanı verebilir". Veremez — daha doğrusu
 * verebilir ama o puanın iki hafta sonra aynı cevaba aynı sayıyı
 * vereceğini garanti edemeyiz. Programın tamamı "aynı tohum aynı
 * sayfa, aynı cevap aynı puan" sözü üzerine kurulu; önce–sonra
 * karşılaştırması o sözün üstünde duruyor.
 *
 * Yazılımın burada yaptığı iş şu: sayfayı üretmek ve uygulayıcıya
 * PUANLAMA ÖLÇÜTLERİNİ vermek. Akıcılık, esneklik, özgünlük ve
 * ayrıntı — dördü de klasik ölçütler ve cevap anahtarında yazıyor.
 * Değerlendiren insan, ama neye bakacağını sayfa söylüyor.
 */

import { Rng } from '../../prng'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { ExerciseTemplate, GradeResult, ItemResult, RenderNode } from '../types'

/** Açık uçlu cevaplarda yapılabilecek tek otomatik denetim. */
function bicimDenetimi(slotlar: string[], answer: unknown): GradeResult {
  const got = Array.isArray(answer) ? answer : []
  const items: ItemResult[] = slotlar.map((id, i) => {
    const ham = got[i]
    const verilen = ham === undefined || ham === null || String(ham) === '' ? null : String(ham)
    return {
      id,
      beklenen: 'açık uçlu — uygulayıcı değerlendirir',
      verilen,
      dogru: verilen !== null && verilen.trim().length >= 3,
    }
  })
  const yazilan = items.filter((x) => x.dogru).length
  return {
    correct: yazilan,
    total: items.length,
    accuracy: items.length ? yazilan / items.length : 0,
    perItem: items.map((x) => x.dogru),
    items,
  }
}

/* ================================================================== *
 * 38. Alternatif kullanım
 *
 * Sıradan bir nesne veriliyor; çocuk onunla yapılabilecek olabildiğince
 * farklı şeyler yazıyor.
 *
 * NESNE SIRADAN OLMAK ZORUNDA. Zaten çok işlevli bir nesne (örneğin
 * "bıçak") verilince görev iraksak düşünme olmaktan çıkıp bilgi
 * hatırlamaya dönüyor: cevaplar nesnenin bilinen kullanımları oluyor.
 * Havuzdaki sekiz nesnenin hepsinin tek bir bariz işlevi var.
 * ================================================================== */
export const alternativeUses: ExerciseTemplate = {
  code: 'alternative_uses',
  title: 'Başka ne işe yarar?',
  domains: ['cognitive_flexibility', 'working_memory_verbal'],
  channels: ['print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Kaç nesne */
      nesne: l >= 6 ? 2 : 1,
      /** Nesne başına kaç satır */
      satir: scale(l, 6, 12),
      /** Önerilen süre (dakika) */
      dakika: 2,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const nesneSayisi = p['nesne'] as number
    const satir = p['satir'] as number

    /** Hepsinin tek bir bariz işlevi var: çok işlevli nesne görevi bozuyor. */
    const NESNELER = ['tuğla', 'ataş', 'gazete', 'ip', 'havlu', 'kaşık', 'mandal', 'şişe']
    const secilen = rng.shuffle(NESNELER).slice(0, nesneSayisi)

    const render: RenderNode[] = []
    const slotlar: string[] = []
    secilen.forEach((nesne, i) => {
      render.push({
        t: 'text',
        value: `${i + 1}. ${nesne.toLocaleUpperCase('tr-TR')} ile başka neler yapılabilir?`,
        size: 'md',
        bold: true,
      })
      render.push({
        t: 'rows',
        items: Array.from({ length: satir }, (_, k) => ({
          label: `${k + 1}.`,
          blank: true,
          id: `a${i}_${k}`,
          genis: true,
        })),
        /*
         * Tek sütun: geniş cevap satırında etiket ile çizgi alt alta
         * geliyor ve iki sütunda numaralar bir satıra, çizgiler başka
         * bir satıra düşüyordu — hangi çizginin hangi numaraya ait
         * olduğu okunmuyordu.
         */
        columns: 1,
      })
      for (let k = 0; k < satir; k++) slotlar.push(`a${i}_${k}`)
    })

    return {
      code: 'alternative_uses',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        `Her nesne için ${p['dakika']} dakikan var. Aklına gelen BAŞKA kullanımları yaz — ` +
        `nesnenin bilinen işi dışında. Birbirinden ne kadar farklı olursa o kadar iyi.`,
      render,
      solution: { slotlar, nesneler: secilen },
      grading: 'manual',
      estimatedSeconds: nesneSayisi * (p['dakika'] as number) * 60,
      itemCount: slotlar.length,
    }
  },
  grade(instance, answer) {
    const sol = instance.solution as { slotlar: string[] }
    return bicimDenetimi(sol.slotlar, answer)
  },
  /**
   * Anahtar bir cevap listesi DEĞİL, puanlama ölçütleri.
   *
   * Uygulayıcı sayfaya bakınca neye bakacağını bilmeli; "doğru cevap
   * şudur" yazan bir anahtar burada yanlış olurdu çünkü doğru cevap
   * yok.
   */
  anahtar(instance) {
    const sol = instance.solution as { nesneler: string[] }
    return (
      `Açık uçlu; doğru cevap yok. Nesneler: ${sol.nesneler.join(', ')}. ` +
      'Puanlama: AKICILIK (kaç kullanım yazdı) · ESNEKLİK (kaç farklı kategori: ' +
      'oyun, inşaat, mutfak gibi) · ÖZGÜNLÜK (başka çocuklarda görmediğiniz cevaplar). ' +
      'Nesnenin bilinen işi sayılmaz.'
    )
  },
}

/* ================================================================== *
 * 39. Yarım çizgiyi tamamla
 *
 * Her kutuda anlamsız birkaç çizgi var; çocuk onları kullanarak bir
 * resim çiziyor ve resme ad veriyor.
 *
 * ÇİZGİLER ANLAMSIZ OLMAK ZORUNDA. Parçanın kendisi bir şeye
 * benziyorsa (ev çatısı, güneş) çoğu çocuk aynı resmi çiziyor ve
 * özgünlük ölçülemiyor. Havuzdaki parçalar soyut: yay, zikzak, köşe,
 * dalga, iki kısa çizgi. Her kutuda ayrıca döndürülüp aynalanıyorlar;
 * aynı parçanın iki kutuda aynı durması, ikinci kutuda ilkini
 * tekrarlamaya davet.
 *
 * AD VERMEK GÖREVİN PARÇASI. Torrance'ta başlık ayrı puanlanıyor:
 * "ağaç" ile "rüzgârda eğilen ağaç" aynı resmin iki ayrı okuması.
 * ================================================================== */
export const incompleteFigures: ExerciseTemplate = {
  code: 'incomplete_figures',
  title: 'Çizimi tamamla',
  domains: ['cognitive_flexibility', 'working_memory_visual'],
  channels: ['print'],
  params(level) {
    const l = clampLevel(level)
    return {
      /** Kaç kutu */
      kutu: scale(l, 4, 8),
      /** Kutu başına kaç çizgi parçası: fazlası serbestliği kısıtlıyor */
      parca: l >= 7 ? 2 : 1,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const kutuSayisi = p['kutu'] as number
    const parcaSayisi = p['parca'] as number

    const items = Array.from({ length: kutuSayisi }, () => ({
      strokes: Array.from({ length: parcaSayisi }, () => yerlestir(rng, rng.pick(PARCALAR))),
    }))

    return {
      code: 'incomplete_figures',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt:
        'Her kutudaki çizgileri kullanarak bir resim çiz. Çizgileri silme, resmin ' +
        'parçası yap. Her resme altındaki satıra bir ad ver.',
      render: [{ t: 'frames', cols: kutuSayisi > 4 ? 4 : 2, items, adSatiri: true }],
      solution: { kutu: kutuSayisi },
      grading: 'manual',
      estimatedSeconds: kutuSayisi * 90,
      itemCount: kutuSayisi,
    }
  },
  grade(instance, answer) {
    const sol = instance.solution as { kutu: number }
    return bicimDenetimi(
      Array.from({ length: sol.kutu }, (_, i) => 'k' + i),
      answer,
    )
  },
  anahtar() {
    return (
      'Açık uçlu; doğru cevap yok. Puanlama: AKICILIK (kaç kutuyu tamamladı) · ' +
      'ÖZGÜNLÜK (başka çocuklarda görmediğiniz resimler) · AYRINTI (resme eklenen ' +
      'ek öğeler) · BAŞLIK (ad resmi anlatıyor mu, yoksa yalnızca adlandırıyor mu). ' +
      'Verilen çizgileri kullanmayan resim akıcılığa sayılmaz.'
    )
  },
}

/* ------------------------------------------------------------------ *
 * Anlamsız çizgi parçaları — 0–1 kutusunda, merkeze göre
 * ------------------------------------------------------------------ */

type Cizgi = [number, number][]

/** Yay: üç noktalı kırık çizgi yeterli, kâğıtta yay gibi okunuyor. */
function yay(): Cizgi {
  return [
    [-0.18, 0.1],
    [0, -0.12],
    [0.18, 0.1],
  ]
}

const PARCALAR: (() => Cizgi)[] = [
  yay,
  // Zikzak
  () => [
    [-0.2, -0.08],
    [-0.07, 0.1],
    [0.07, -0.08],
    [0.2, 0.1],
  ],
  // Köşe
  () => [
    [-0.16, 0.14],
    [-0.16, -0.14],
    [0.14, -0.14],
  ],
  // Dalga
  () => [
    [-0.22, 0],
    [-0.11, -0.1],
    [0, 0],
    [0.11, 0.1],
    [0.22, 0],
  ],
  // Tek eğik çizgi
  () => [
    [-0.16, 0.14],
    [0.16, -0.14],
  ],
  // Yarım kutu
  () => [
    [-0.14, -0.14],
    [0.14, -0.14],
    [0.14, 0.14],
  ],
]

/**
 * Parçayı kutuya yerleştirir: döndür, aynala, kaydır.
 *
 * Aynı parça iki kutuda aynı dursaydı çocuk ikinci kutuda birinciyi
 * tekrarlamaya davet edilirdi ve özgünlük ölçülemezdi.
 */
function yerlestir(rng: Rng, parcaUret: () => Cizgi): Cizgi {
  const aci = (rng.int(0, 7) * Math.PI) / 4
  const ayna = rng.int(0, 1) === 1 ? -1 : 1
  // Merkez kenara fazla yaklaşmasın: parça yarıçapı en fazla 0.22
  const mx = 0.3 + rng.float() * 0.4
  const my = 0.3 + rng.float() * 0.4
  return parcaUret().map(([x, y]) => {
    const sx = x * ayna
    const dx = sx * Math.cos(aci) - y * Math.sin(aci)
    const dy = sx * Math.sin(aci) + y * Math.cos(aci)
    return [kirp(mx + dx), kirp(my + dy)] as [number, number]
  })
}

/** Kutunun dışına taşmasın: dışarı taşan çizgi basılınca kesiliyor. */
function kirp(v: number): number {
  return Math.min(0.94, Math.max(0.06, Number(v.toFixed(4))))
}
